import React, {forwardRef, useEffect, useRef, useState} from 'react';

import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './checkbox.component.module.styl';
import CheckboxSvg from './svg';

import type {ComponentProps, ReactNode} from 'react';
import mergeRefs from 'merge-refs';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);


type NativeProps = Omit<ComponentProps<'input'>, 'ref' | 'onChange'>;

export interface CheckBoxProps extends NativeProps {
    /**
     * Name attribute of checkbox
     */
    name?: string,
    /**
     * Checked property of checkbox
     */
    checked?: boolean,

    /**
     * Custom type of checkbox
     */
    custom?: boolean,

    /**
     * Disabled attribute of checkbox
     */
    disabled?: boolean,

    /**
     * Should highlight label to make it look like a link
     */
    highlightedLabel?: boolean,

    /**
     * Indeterminate attribute of checkbox
     */
    indeterminate?: boolean,

    /**
     * Main checkbox is semibold
     */
    main?: boolean,

    /**
     * @deprecated It will be removed in 6 months 09-15-2025. onChange callback on checkbox change, use onCheckBoxChange
     */
    onChange?: (checked: boolean, indeterminate: boolean, name: string) => void,

    /**
     *Callback on checkbox change
     */
    onCheckBoxChange?: (changeData: {
        event: React.ChangeEvent<HTMLInputElement>,
        name: string,
    }) => void,

    /**
     * If yes - the switch will not change its state by click, only the parent onChange will be called
     */
    usePropsOnly?: boolean,

    /**
     * ClickTrough ability
     */
    clickTrough?: boolean,

    /**
     * Checkbox label
     */
    label?: ReactNode | string,

    /**
     * ClassName for checkbox
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mix?: string,

    /**
     * ClassName for pointer
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mixPointer?: string,
    /**
     * ClassName for pointer
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mixBox?: string,
    /**
     * ClassName for pointer
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mixLabel?: string,
    /**
     * ClassName for pointer
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mixHandler?: string,

    /**
     * Checkbox label ellipsis type
     */
    ellipsisType?: EllipsisType,
    /**
     * Checkbox wrapper props
     */
    rootProps?: ComponentProps<'div'>,
    /**
     ref to root element
     */
    rootRef?: React.Ref<HTMLDivElement>,
    /**
     * Checkbox label props
     */
    labelProps?: ComponentProps<'label'>,
    /**
     ref to label element
     */
    labelRef?: React.Ref<HTMLLabelElement>,
    /**
     ref to input element
     */
    ref?: React.Ref<HTMLInputElement>,
}

export const EllipsisTypes = {
    END: 'END',
    NONE: 'NONE',
} as const;


type EllipsisType = keyof typeof EllipsisTypes;


/**
 * @description The component created to allow a user to make multiple choices that are broadly used in forms and surveys.
 * Checkboxes are used to select one or several options in a list. Checkboxes have 8px of padding before the label.
 * ### Accessibility
 * This component implements the WAI-ARIA authoring practices for a checkbox role.
 * For more information, see [WAI-ARIA Authoring Practices](https://www.w3.org/WAI/ARIA/apg/patterns/checkbox/).
 *
 * @see [Storybook]{@link
 * https://storybook.dev.pitchbookdata.com/?path=/story/forms-checkbox--check-box-default}
 */
export const CheckBox = forwardRef<HTMLInputElement, CheckBoxProps>(function CheckBox(
    props, ref) {
    const {
        checked = false,
        onChange = () => undefined,
        onCheckBoxChange = () => undefined,
        disabled = false,
        highlightedLabel = false,
        indeterminate = false,
        usePropsOnly = false,
        custom = false,
        main = false,
        name = '',
        ellipsisType = EllipsisTypes.END,
        label,
        clickTrough,
        mix,
        mixPointer,
        mixBox,
        mixLabel,
        mixHandler,
        rootProps,
        rootRef,
        labelProps,
        labelRef,
        ...inputProps
    } = props;

    const [isChecked, setIsChecked] = useState<boolean>(checked);
    const [isIndeterminate, setIsIndeterminate] = useState<boolean>(indeterminate);
    const [isPressed, setIsPressed] = useState<boolean>(false);

    const innerRef = useRef<HTMLInputElement>(null);

    const setIndeterminate = () => {
        if (innerRef && innerRef.current) {
            innerRef.current.indeterminate = isIndeterminate;
        }
    };

    useEffect(() => {
        setIndeterminate();
    }, [isIndeterminate]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsIndeterminate(indeterminate);
    }, [indeterminate]);

    useEffect(() => {
        setIsChecked(Boolean(checked));
    }, [checked]);

    const onMouseDown = () => {
        if (!disabled) {
            setIsPressed(true);
        }
    };

    const onMouseOut = () => {
        if (!disabled) {
            setIsPressed(false);
        }
    };

    const onHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {checked: eventChecked, indeterminate: eventIndeterminate} = event.currentTarget;

        if (!usePropsOnly) {
            setIsChecked(eventChecked);
            setIsIndeterminate(false);
        }

        onChange?.(eventChecked, eventIndeterminate, name || '');
        onCheckBoxChange?.({
            event,
            name: name || '',
        });
    };

    const componentClasses = classNames(
        {
            'checkbox-button_custom': custom,
            'checkbox-button_main': main,
            'checkbox-button_click-through': clickTrough,
            'checkbox-button_disabled': disabled,
        },
        'checkbox-button',
        mix,
        rootProps?.className,
    );

    const handlerClasses = classNames(
        'checkbox-button__handler',
        mixHandler,
        inputProps.className,
        {
            'checkbox-button__handler_pressed': isPressed,
            'checkbox-button__handler_checked': isChecked,
            'checkbox-button__handler_disabled': disabled,
        },
    );

    const checkboxClasses = classNames(
        'checkbox-button__pointer',
        mixPointer,
        {
            'checkbox-button__pointer_only': !label && !custom,
        },
    );

    const labelClasses = classNames('checkbox-button__label', mixLabel, {
        'checkbox-button__label_highlighted': highlightedLabel,
    });

    let ellipsisLabel = null;

    if (label && ellipsisType === EllipsisTypes.END) {
        ellipsisLabel = <EllipsisTextHint mix={labelClasses}>{label}</EllipsisTextHint>;
    } else if (label && ellipsisType === EllipsisTypes.NONE) {
        ellipsisLabel = label;
    }

    const boxClassNames = classNames('checkbox-button__box', mixBox, labelProps?.className);

    return (
        <div
            ref={rootRef}
            {...rootProps}
            className={componentClasses}>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <label
                ref={labelRef}
                {...labelProps}
                className={boxClassNames}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseOut}
                onBlur={onMouseOut}>
                <input
                    {...inputProps}
                    ref={mergeRefs(innerRef, ref)}
                    className={handlerClasses}
                    disabled={disabled}
                    checked={isChecked}
                    onChange={onHandleChange}
                    name={name}
                    type="checkbox"
                />
                <div className={classes['checkbox-button__caption']}>
                    <span className={checkboxClasses}>
                        {CheckboxSvg({
                            disabled,
                            checked: isChecked,
                            indeterminate: isIndeterminate,
                            pressed: isPressed,
                        }, custom)}
                    </span>
                    {ellipsisLabel}
                </div>
            </label>
        </div>
    );
});

CheckBox.displayName = 'CheckBox';
